import { useCallback, useEffect, useState } from "react";
import { Copy, Loader2, Share2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";

export default function SettingsReferrals() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [referralProgram, setReferralProgram] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc("tenant_get_referral_program");
    setLoading(false);
    if (rpcError) {
      setError(rpcError.message || "Could not load referrals.");
      return;
    }
    setReferralProgram(data || null);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-12 text-center" data-testid="settings-referrals-loading">
        <Loader2 className="w-5 h-5 mx-auto animate-spin text-slate-400" />
        <p className="mt-3 text-sm text-slate-500">Loading referrals...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-sm text-rose-700" data-testid="settings-referrals-error">
        {error}
      </div>
    );
  }

  if (!referralProgram?.enabled) {
    return (
      <div className="space-y-6" data-testid="settings-referrals">
        <PageHeader />
        <section className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-center shadow-sm" data-testid="referral-program-disabled">
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400">
            <Share2 className="h-5 w-5" />
          </div>
          <h2 className="mt-3 text-sm font-semibold text-slate-900">Referrals are not available yet.</h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
            When Dental OS enables the referral program for your clinic, your share link and earned credits will appear here.
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-referrals" data-tutorial-id="settings-referrals">
      <PageHeader />
      <ReferralProgramSection referralProgram={referralProgram} />
    </div>
  );
}

function PageHeader() {
  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900">Referrals</h1>
      <p className="mt-1 text-sm text-slate-500">
        Share Dental OS with another clinic and track free-month credits earned from successful referrals.
      </p>
    </div>
  );
}

function ReferralProgramSection({ referralProgram }) {
  const code = referralProgram?.code || {};
  const stats = referralProgram?.stats || {};
  const history = Array.isArray(referralProgram?.history) ? referralProgram.history : [];
  const referralLink = `${window.location.origin}/ref/${code.code || ""}`;
  const unappliedMonths = Number(stats.unapplied_free_months || 0);

  async function copyLink() {
    try {
      await navigator.clipboard?.writeText(referralLink);
    } catch {
      const input = document.createElement("input");
      input.value = referralLink;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      input.remove();
    }
  }

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="referral-program-section">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Referral Program</p>
          <h2 className="mt-1 text-lg font-semibold text-slate-950">Earn free months</h2>
          <p className="mt-1 text-xs text-slate-500">Credits apply automatically to future invoices.</p>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Your referral link</p>
        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
          <div className="min-w-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-700" data-testid="referral-link">
            {referralLink}
          </div>
          <button
            type="button"
            onClick={copyLink}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            data-testid="copy-referral-link"
          >
            <Copy className="h-4 w-4" />
            Copy
          </button>
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <Stat label="Referrals sent" value={stats.total_referrals_sent ?? 0} />
        <Stat label="Converted" value={stats.converted ?? 0} />
        <Stat label="Free months earned" value={stats.free_months_earned ?? 0} />
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
        <div className="border-b border-slate-200 px-4 py-3">
          <p className="text-sm font-semibold text-slate-950">Referral history</p>
        </div>
        <div className="divide-y divide-slate-100">
          {history.length ? history.map((row) => (
            <div key={row.id} className="grid grid-cols-[1fr_auto] gap-3 px-4 py-3 sm:grid-cols-[1fr_8rem_7rem]">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">{row.clinic_name || "Clinic"}</p>
              </div>
              <p className="text-xs text-slate-500">{dateOnly(row.signup_date)}</p>
              <div className="text-right"><ReferralStatusBadge status={row.status} /></div>
            </div>
          )) : (
            <p className="px-4 py-6 text-center text-sm text-slate-500">No referrals yet.</p>
          )}
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-950">
        You have <strong>{unappliedMonths} free {unappliedMonths === 1 ? "month" : "months"}</strong> of unused referral credit.
      </div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-1 text-xl font-bold tabular-nums text-slate-950">{value}</p>
    </div>
  );
}

function ReferralStatusBadge({ status }) {
  const normalized = status === "converted" || status === "rewarded" ? "rewarded" : status;
  const classes = {
    pending: "border-slate-200 bg-slate-100 text-slate-700",
    trial: "border-amber-200 bg-amber-50 text-amber-800",
    rewarded: "border-emerald-200 bg-emerald-50 text-emerald-800",
  };
  const labels = {
    pending: "Pending",
    trial: "Trial",
    rewarded: "Rewarded",
  };
  return (
    <span className={`inline-flex rounded-full border px-2 py-1 text-[11px] font-semibold ${classes[normalized] || classes.pending}`}>
      {labels[normalized] || "Pending"}
    </span>
  );
}

function dateOnly(value) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return String(value).slice(0, 10);
  }
}
